import { Box, Button, Flex, Image, Text, Divider, useTheme } from '@chakra-ui/react';
import styled from '@emotion/styled/macro';
import { Overlay } from 'components';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink, useLocation } from 'react-router-dom';
import { RootState } from 'store';
import { getTypes, hasType } from 'utils/account-type';
import { isCorporateThemeSelector } from 'modules/app/appSlice';
import qs from 'qs';
import { rgba } from 'polished';

// Components
import MainLogo from '../MainLogo';
import AvatarMenu from './AvatarMenu';
import ContextSwitcher from './ContextSwitcher';
import NavBar from './Menu';
import MenuDrawer from './MenuDrawer';
import LanguageSwitcher from './LanguageSwitcher';
import { mq } from 'styles/breakpoints';
import { ReactComponent as LogoutIcon } from 'assets/svgs/logout.svg';
import { setIsAuthenticated, signInOrSignUpGuest } from 'modules/auth/authSlice';
import { isRequiredBillingInformationMissingSelector, resetUser, switchProfileTo } from 'modules/user/userSlice';
import ChooseContextModal from 'components/ChooseContextModal/ChooseContextModal';
import SettingsMenu from './corporate/SettingsMenu';
import MenuBox from './corporate/MenuBox';
import MenuDropdown from './corporate/MenuDropdown';
import MobileMenu from './corporate/MobileMenu';
import CorporateContextSwitcher from './corporate/ContextSwitcher';

const ClickableLogo = styled(NavLink)`
  display: inline-flex;
  text-decoration: none;
`;

interface HeaderProps {
  menu?: MenuItem[];
  settingsProfileMenu?: MenuItem[];
  settingsCompanyMenu?: MenuItem[];
}

const Header: FC<HeaderProps> = ({ menu, settingsProfileMenu, settingsCompanyMenu }) => {
  const { colors } = useTheme();
  const { ready, t } = useTranslation('common');
  const location = useLocation();
  const { isAuthenticated: isUserAuthenticated } = useSelector((state: RootState) => state.auth);
  const { accounts, currentProfile } = useSelector((state: RootState) => state.user);
  const logo = useSelector((state: RootState) => state.app.logo);
  const context = useSelector((state: RootState) => state.app.context);
  const isCorporateTheme = useSelector(isCorporateThemeSelector);
  const languages = useSelector((state: RootState) => state.app.languages);
  const isRequiredBillingInformationMissing = useSelector(isRequiredBillingInformationMissingSelector);
  const partnerName = process.env.REACT_APP_PARTNER_NAME || 'chooose';
  const isAuthenticated = isUserAuthenticated && !!accounts && accounts.length > 0;
  const isGuestCertificate = location.pathname.startsWith('/guest/certificate');
  const search = qs.parse(location.search, { ignoreQueryPrefix: true });
  const [isModalShown, setIsModalShown] = useState(false);

  const isGuestMyChooose = hasType(getTypes(accounts), ['GuestMyChooose']);
  const isGuestWeChooose = hasType(getTypes(accounts), ['GuestWeChooose']);

  const isWeChoooseOnly = isGuestWeChooose && !isGuestMyChooose;
  const isMyChoooseOnly = isGuestMyChooose && !isGuestWeChooose;
  const isConnectContext = context === 'connect';

  const dispatch = useDispatch();

  const goToSignInSignUp = (): void => {
    if (isWeChoooseOnly) {
      dispatch(setIsAuthenticated(false));
      dispatch(resetUser());
      dispatch(signInOrSignUpGuest(`?inviteId=${search.inviteId}&claimCode=${search.claimCode}` + '&context=wechooose', 'wechooose'));
    } else if (isMyChoooseOnly) {
      dispatch(setIsAuthenticated(false));
      dispatch(resetUser());
      dispatch(signInOrSignUpGuest(`?inviteId=${search.inviteId}&claimCode=${search.claimCode}` + '&context=mychooose', 'mychooose'));
    } else {
      setIsModalShown(true);
    }
  };

  const contextItems: ContextMenuItem[] = [
    {
      label: 'We',
      color: colors.pink[500],
      value: 'wechooose',
      hasAccess: hasType(getTypes(accounts), ['WeChooose']),
      title: isCorporateTheme ? t('contextSwitcher.corporateWeChoooseTitle') : t('contextSwitcher.weChoooseTitle'),
      testId: 'we-chooose-text',
    },
    {
      label: 'We',
      color: colors.pink[500],
      value: 'guest-wechooose',
      hasAccess: hasType(getTypes(accounts), ['GuestWeChooose']),
      title: isCorporateTheme ? t('contextSwitcher.corporateWeChoooseTitle') : t('contextSwitcher.weChoooseTitle'),
      testId: 'we-chooose-text',
    },
    {
      label: 'Connect',
      color: colors.pink[200],
      title: isCorporateTheme ? t('contextSwitcher.corporateConnectTitle') : t('contextSwitcher.connectTitle'),
      testId: 'chooose-connect-text',
      inverted: true,
      value: 'connect',
      hasAccess: hasType(getTypes(accounts), ['WeChoooseCommmunity']),
    },
    {
      label: 'My',
      color: colors.pink[200],
      value: 'mychooose',
      title: isCorporateTheme ? t('contextSwitcher.corporateMyChoooseTitle') : t('contextSwitcher.myChoooseTitle'),
      testId: 'my-chooose-text',
      hasAccess: hasType(getTypes(accounts), ['MyChooose']),
    },
    {
      label: 'My',
      color: colors.pink[200],
      value: 'guest-mychooose',
      title: isCorporateTheme ? t('contextSwitcher.corporateMyChoooseTitle') : t('contextSwitcher.myChoooseTitle'),
      testId: 'my-chooose-text',
      hasAccess: hasType(getTypes(accounts), ['GuestMyChooose']),
    },
  ];

  const accessibleContext = contextItems.filter(f => f.hasAccess);
  const filteredMenuItems = (menu ?? []).filter((item: MenuItem) => !item.hide);
  const profileItems = (settingsProfileMenu ?? []).filter(w => w.hasAccess);
  const companyItems = (settingsCompanyMenu ?? []).filter(w => w.hasAccess);

  // @TODO: This is a temporary solution
  const partnersWithWideLogo = ['clarksons'];

  // @TODO: This is a temporary solution
  const partnersWithHighLogo = ['iberia', "let's fly neutral cargo"];

  const handleProfileType = (type: ProfileType) => {
    if (type === currentProfile?.type) return;
    dispatch(switchProfileTo(type));
  };

  const LogoImage = logo ? (
    <Image
      src={logo}
      alt="logo"
      ignoreFallback
      sx={{
        w: partnersWithWideLogo.includes(partnerName.toLowerCase()) ? '240px' : '114px',
        objectFit: 'contain',
        [mq.sm]: { w: partnersWithWideLogo.includes(partnerName.toLowerCase()) ? '295px' : '120px' },
      }}
    />
  ) : (
    <MainLogo color={isCorporateTheme ? 'blue' : 'pink'} />
  );

  const WorkingWith = (
    <>
      <Text color={rgba(colors.white, 0.6)} fontSize="xs">
        {t('profileMenu.workingWith')}
      </Text>
      <Flex
        flexDir="column"
        justifyContent="center"
        bgColor={colors.white}
        h="24px"
        maxW={partnersWithWideLogo.includes(partnerName.toLowerCase()) ? '130px' : '70px'}
        p="2px"
        borderRadius="base"
        sx={{
          '& > img': {
            h: partnersWithHighLogo.includes(partnerName.toLowerCase()) ? 'initial' : '22px',
          },
        }}
      >
        {LogoImage}
      </Flex>
    </>
  );

  return (
    <>
      <ChooseContextModal isOpen={isModalShown} onClose={(): void => setIsModalShown(false)} />
      <Box
        as="header"
        layerStyle="header"
        boxShadow="header"
        px={isCorporateTheme ? 0 : '35px'}
        mx={isCorporateTheme ? '10px' : 0}
        bgColor={
          isCorporateTheme
            ? isAuthenticated && !isRequiredBillingInformationMissing && !isGuestCertificate
              ? 'secondary.800'
              : 'transparent'
            : 'white'
        }
        borderRadius={isCorporateTheme ? '12px' : 0}
      >
        <Flex
          layerStyle="navWidth"
          m="0 auto"
          h="100%"
          justifyContent={isAuthenticated && !isRequiredBillingInformationMissing ? 'flex-start' : 'center'}
          position="relative"
        >
          {!ready ? (
            <Overlay overlayHeight="100%" type="header" />
          ) : (
            <>
              {isCorporateTheme ? (
                (isGuestCertificate || isRequiredBillingInformationMissing) && LogoImage
              ) : isGuestCertificate || isRequiredBillingInformationMissing ? (
                LogoImage
              ) : (
                <ClickableLogo exact to="/">
                  {LogoImage}
                </ClickableLogo>
              )}
              {isAuthenticated && !isGuestCertificate && !isRequiredBillingInformationMissing ? (
                <>
                  <NavBar items={filteredMenuItems} />
                  {isCorporateTheme && <MenuDropdown items={filteredMenuItems} />}
                  <Flex ml="auto" align="center" justify="center">
                    {isCorporateTheme ? (
                      <>
                        <Box display={{ base: 'none', lg: 'flex' }} alignItems="center" height="100%" m={{ base: 0, lg: '0 17px 0 20px' }}>
                          {!isConnectContext && (
                            <>
                              <Box>{WorkingWith}</Box>
                              <Divider orientation="vertical" mx="20px" opacity="0.15" />
                            </>
                          )}
                          <MenuBox items={companyItems} handleProfileType={handleProfileType} profileType="Company" mr="30px" />
                          <MenuBox items={profileItems} handleProfileType={handleProfileType} profileType="Person" />
                          {accessibleContext.length > 1 && (
                            <>
                              <Divider orientation="vertical" m="0 28px 0 20px" opacity="0.15" />
                              <CorporateContextSwitcher items={accessibleContext} current={context} />
                            </>
                          )}
                          {languages && languages.length > 1 && (
                            <>
                              <Divider orientation="vertical" m="0 28px 0 20px" opacity="0.15" />
                              <SettingsMenu languages={languages} />
                            </>
                          )}
                        </Box>
                        {!isConnectContext && (
                          <Box d={{ base: 'block', lg: 'none' }} mr="12px">
                            {WorkingWith}
                          </Box>
                        )}
                        <MobileMenu
                          profileItems={profileItems}
                          companyItems={companyItems}
                          contextItems={accessibleContext}
                          currentContext={context}
                          languages={languages}
                        />
                      </>
                    ) : (
                      <>
                        <Box mr="15px" display={['none', 'none', 'none', 'none', 'block']} position="relative">
                          <ContextSwitcher items={accessibleContext} fullSize current={context} />
                        </Box>
                        <Box mr={['-25px', '-25px', '-25px', '-25px', '-10px']} display={['none', 'none', 'none', 'none', 'block']}>
                          <AvatarMenu profileItems={profileItems} companyItems={companyItems} />
                        </Box>
                        <MenuDrawer
                          menuItems={filteredMenuItems}
                          profileItems={profileItems}
                          companyItems={companyItems}
                          contextItems={accessibleContext}
                        />
                        {isCorporateTheme && languages && languages.length > 1 && (
                          <Box display={{ base: 'none', lg: 'block' }}>
                            <LanguageSwitcher />
                          </Box>
                        )}
                      </>
                    )}
                  </Flex>
                </>
              ) : null}
              {isGuestCertificate && (
                <>
                  <Flex ml="auto" align="center">
                    <Button
                      leftIcon={<LogoutIcon />}
                      variant="outline"
                      onClick={goToSignInSignUp}
                      height={{ base: '38px', lg: '50px' }}
                      px={{ base: '8px', lg: '24px' }}
                    >
                      <Text as="span" ml={{ base: '5px', lg: '11px' }}>
                        {t('signInOrCreateAccount')}
                      </Text>
                    </Button>
                  </Flex>
                </>
              )}
            </>
          )}
        </Flex>
      </Box>
    </>
  );
};

export default Header;
